import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiClient, AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { ShoppingCart, Image as ImageIcon, Star, Lightning } from '@phosphor-icons/react';

const StarRating = ({ rating, onRate, size = 20, interactive = false }) => (
  <div className="flex gap-1">
    {[1, 2, 3, 4, 5].map((star) => (
      <button key={star} onClick={() => interactive && onRate(star)} className={interactive ? 'cursor-pointer' : 'cursor-default'}>
        <Star size={size} weight={star <= rating ? 'fill' : 'regular'} className={star <= rating ? 'text-yellow-400' : 'text-slate-600'} />
      </button>
    ))}
  </div>
);

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, fetchCartCount } = useContext(AuthContext);
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customizationType, setCustomizationType] = useState('corporate');
  const [customText, setCustomText] = useState('');
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedSize, setSelectedSize] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [reviews, setReviews] = useState([]);
  const [avgRating, setAvgRating] = useState(0);
  const [totalReviews, setTotalReviews] = useState(0);
  const [newRating, setNewRating] = useState(5);
  const [newComment, setNewComment] = useState('');

  useEffect(() => { fetchProduct(); }, [id]);

  const fetchProduct = async () => {
    try {
      const response = await apiClient.get(`/products/${id}`);
      setProduct(response.data);
      if (response.data.sizes.length > 0) setSelectedSize(response.data.sizes[0]);
      fetchReviews(id);
    } catch (error) { toast.error('Product not found'); navigate('/products'); }
    finally { setLoading(false); }
  };

  const fetchReviews = async (productId) => {
    try {
      const response = await apiClient.get(`/reviews/${productId}`);
      setReviews(response.data.reviews);
      setAvgRating(response.data.average_rating);
      setTotalReviews(response.data.total_reviews);
    } catch (error) { console.error('Error fetching reviews'); }
  };

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) { const reader = new FileReader(); reader.onloadend = () => setImagePreview(reader.result); reader.readAsDataURL(file); }
  };

  const createCustomization = async () => {
    if (customText || imagePreview) {
      const res = await apiClient.post('/customizations', {
        customization_type: customizationType, custom_text: customText || null,
        uploaded_image_url: imagePreview || null, text_font: 'Playfair Display', text_color: '#3B82F6'
      });
      return res.data.id;
    }
    return null;
  };

  const handleAddToCart = async () => {
    if (!user) { toast.error('Please login to add items to cart'); return; }
    if (!selectedSize) { toast.error('Please select a size'); return; }
    try {
      const customizationId = await createCustomization();
      await apiClient.post('/cart', { product_id: product.id, customization_id: customizationId, quantity, size: selectedSize });
      toast.success('Added to cart!');
      fetchCartCount();
    } catch (error) { toast.error('Failed to add to cart'); }
  };

  const handleBuyNow = async () => {
    if (!user) { toast.error('Please login to place order'); return; }
    if (!selectedSize) { toast.error('Please select a size'); return; }
    try {
      const customizationId = await createCustomization();
      // Add to cart first then navigate to checkout
      await apiClient.post('/cart', { product_id: product.id, customization_id: customizationId, quantity, size: selectedSize });
      fetchCartCount();
      navigate('/checkout');
    } catch (error) { toast.error('Failed to proceed. Please try again.'); }
  };

  const submitReview = async () => {
    if (!user) { toast.error('Please login to leave a review'); return; }
    if (!newComment.trim()) { toast.error('Please write a comment'); return; }
    try {
      await apiClient.post('/reviews', { product_id: id, rating: newRating, comment: newComment });
      toast.success('Review submitted!');
      setNewComment(''); setNewRating(5);
      fetchReviews(id);
    } catch (error) { toast.error('Failed to submit review'); }
  };

  if (loading || !product) return <div className="min-h-screen pt-24 flex items-center justify-center"><p className="text-slate-400">Loading product...</p></div>;

  return (
    <div className="min-h-screen pt-24 pb-12">
      <div className="container mx-auto px-8 md:px-16 lg:px-24">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Product Image & Info */}
          <div className="space-y-6" data-testid="product-preview-section">
            <div className="aspect-square bg-cover bg-center border border-border relative overflow-hidden"
              style={{ backgroundImage: `url('${product.image_url}')`, backgroundSize: 'contain', backgroundRepeat: 'no-repeat', backgroundColor: '#0A0F1C' }}
              data-testid="customization-preview">
              <div className="absolute inset-0 flex flex-col items-center justify-center p-8 space-y-4">
                {imagePreview && <img src={imagePreview} alt="Custom upload" className="max-w-[200px] max-h-[200px] object-contain" data-testid="uploaded-image-preview" />}
                {customText && <p className="font-heading text-2xl text-[#D4AF37]" data-testid="custom-text-preview">{customText}</p>}
              </div>
            </div>
            <div className="bg-card border border-border p-6" data-testid="product-info-card">
              <div className="flex items-center gap-3 mb-3">
                <StarRating rating={Math.round(avgRating)} size={18} />
                <span className="text-sm text-slate-400">({totalReviews} reviews)</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-medium mb-3 text-foreground" data-testid="product-name">{product.name}</h1>
              <p className="text-3xl font-medium text-[#D4AF37] mb-4" data-testid="product-price">${product.base_price.toFixed(2)}</p>
              <p className="text-base text-slate-300 leading-relaxed" data-testid="product-description">{product.description}</p>
            </div>
            <div className="bg-card border border-border p-6" data-testid="product-features">
              <h3 className="text-lg font-normal mb-4 text-foreground">Features</h3>
              <ul className="space-y-2">
                {product.features.map((feature, index) => (
                  <li key={index} className="flex items-start space-x-2"><span className="text-[#D4AF37] mt-1">•</span><span className="text-sm text-slate-300">{feature}</span></li>
                ))}
              </ul>
            </div>
          </div>

          {/* Right: Customization + Cart */}
          <div className="space-y-6" data-testid="customization-section">
            <div className="bg-card border border-border p-8">
              <h2 className="text-xl font-normal mb-6 text-foreground" data-testid="customization-heading">Customize Your Bottle</h2>
              <div className="mb-6">
                <Label className="mb-3 block">Customization Type</Label>
                <Tabs value={customizationType} onValueChange={setCustomizationType}>
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="corporate">Corporate</TabsTrigger>
                    <TabsTrigger value="wedding">Wedding</TabsTrigger>
                    <TabsTrigger value="personal">Personal</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              <div className="mb-6">
                <Label htmlFor="image-upload" className="mb-3 block">Upload Logo or Photo</Label>
                <div className="border-2 border-dashed border-border rounded-sm p-6 text-center hover:border-primary/50 transition-colors">
                  <input id="image-upload" type="file" accept="image/*" onChange={handleImageUpload} className="hidden" data-testid="image-upload-input" />
                  <label htmlFor="image-upload" className="cursor-pointer">
                    <ImageIcon size={40} weight="thin" className="mx-auto mb-2 text-slate-400" />
                    <p className="text-sm text-slate-400">{imagePreview ? 'Image uploaded' : 'Click to upload'}</p>
                  </label>
                </div>
              </div>
              <div className="mb-6">
                <Label htmlFor="custom-text" className="mb-3 block">Custom Text</Label>
                <Input id="custom-text" placeholder="Company name, couple names, etc." value={customText} onChange={(e) => setCustomText(e.target.value)} data-testid="custom-text-input" />
              </div>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="mb-3 block">Size</Label>
                  <Select value={selectedSize} onValueChange={setSelectedSize}>
                    <SelectTrigger data-testid="size-select-trigger"><SelectValue placeholder="Select size" /></SelectTrigger>
                    <SelectContent>
                      {product.sizes.map((size) => (<SelectItem key={size} value={size}>{size}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="quantity" className="mb-3 block">Quantity</Label>
                  <Input id="quantity" type="number" min="1" value={quantity} onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} data-testid="quantity-input" />
                </div>
              </div>

              {/* TWO BUTTONS: Add to Cart + Buy Now */}
              <div className="space-y-3">
                <Button onClick={handleBuyNow} size="lg" className="w-full rounded-sm bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-white transition-transform duration-300 hover:-translate-y-1" data-testid="buy-now-button">
                  <Lightning size={20} weight="fill" className="mr-2" /> Buy Now
                </Button>
                <Button onClick={handleAddToCart} size="lg" variant="outline" className="w-full rounded-sm" data-testid="add-to-cart-button">
                  <ShoppingCart size={20} weight="regular" className="mr-2" /> Add to Cart
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-16" data-testid="reviews-section">
          <h2 className="text-2xl sm:text-3xl font-medium mb-8 text-foreground">Customer Reviews</h2>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-1">
              <div className="bg-card border border-border p-6" data-testid="write-review-card">
                <h3 className="text-lg font-normal mb-4 text-foreground">Write a Review</h3>
                <div className="mb-4">
                  <Label className="mb-2 block">Your Rating</Label>
                  <StarRating rating={newRating} onRate={setNewRating} size={28} interactive={true} />
                </div>
                <div className="mb-4">
                  <Label className="mb-2 block">Your Review</Label>
                  <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} rows={4} placeholder="Share your experience..."
                    className="flex w-full rounded-sm border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-[#D4AF37]"
                    data-testid="review-comment-input" />
                </div>
                <Button onClick={submitReview} className="w-full rounded-sm" data-testid="submit-review-btn">Submit Review</Button>
              </div>
            </div>
            <div className="lg:col-span-2 space-y-4" data-testid="reviews-list">
              {reviews.length === 0 ? (
                <div className="bg-card border border-border p-8 text-center"><p className="text-slate-400">No reviews yet. Be the first!</p></div>
              ) : reviews.map((review, index) => (
                <div key={review.id} className="bg-card border border-border p-6" data-testid={`review-${index}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-[#D4AF37]/20 flex items-center justify-center text-[#D4AF37] text-sm font-medium">
                        {review.user_name?.charAt(0)?.toUpperCase() || 'C'}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground">{review.user_name}</p>
                        <p className="text-xs text-slate-500">{new Date(review.created_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <StarRating rating={review.rating} size={16} />
                  </div>
                  <p className="text-sm text-slate-300">{review.comment}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
